import {
  VacanciesTemplatesForm,
  type VacanciesTemplatesFormValues,
} from '../vacanciesTemplatesForm';
import { useNavigate, useParams } from 'react-router';
import {
  useGetVacancyTemplate,
  useUpdateVacancyTemplate,
  type VacancyTemplate,
} from '@entities/vacancy';
import { QueryKey } from '@shared/config';
import { showNotification } from '@shared/utils';
import { useQueryClient } from '@tanstack/react-query';
import { useVacanciesTemplatesConfirmCancelSaveModal } from '../modals';
import { getTagsByGroupsForCreate } from '@shared/helpers';
import { useGetSkill } from '@entities/skill';

export const VacanciesTemplatesEdit = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const { data: vacancyTemplate } = useGetVacancyTemplate(Number(id));

  const { mutate: updateVacancyTemplate, isPending } = useUpdateVacancyTemplate(
    Number(id)
  );

  const { openConfirmCancelSaveModal } =
    useVacanciesTemplatesConfirmCancelSaveModal();

  const handleBackHandler = (isDirty: boolean) => {
    if (isDirty) {
      openConfirmCancelSaveModal();
    } else {
      navigate('/vacancies-templates-list');
    }
  };

  const { data: skills } = useGetSkill();

  const handleSubmit = (values: VacanciesTemplatesFormValues) => {
    updateVacancyTemplate(
      {
        ...values,
        currencyId: Number(values.currencyId),
        salaryFrom: Number(values.salaryFrom),
        salaryTo: Number(values.salaryTo),
        paymentPeriodId: Number(values.paymentPeriodId),
        employmentTypeIds: values.employmentTypeIds?.map(Number),
        scheduleWorkIds: values.scheduleWorkIds?.map(Number),
        skills: getTagsByGroupsForCreate({
          directory: skills || [],
          selectedDirectory: values.skills,
        }),
        isActive: vacancyTemplate?.isActive || false,
      },
      {
        onSuccess: data => {
          queryClient.setQueryData(
            [QueryKey.GET_VACANCIES_TEMPLATES, Number(id)],
            (prevData: VacancyTemplate) => {
              return {
                ...prevData,
                ...data,
              };
            }
          );

          queryClient.invalidateQueries({
            queryKey: [QueryKey.GET_SKILLS],
          });
        },
        onError: error => {
          showNotification({
            error,
            variant: 'error',
            message:
              error.response?.data.message ||
              'Ошибка при редактировании шаблона',
          });
        },
      }
    );
  };

  return (
    <VacanciesTemplatesForm
      onBackHandler={handleBackHandler}
      onSubmit={handleSubmit}
      initialValues={{
        name: vacancyTemplate?.name || '',
        salaryFrom: vacancyTemplate?.salaryFrom,
        salaryTo: vacancyTemplate?.salaryTo,
        currencyId: vacancyTemplate?.currency?.id.toString() || '',
        employmentTypeIds:
          vacancyTemplate?.employmentTypes?.map(({ id }) => id.toString()) ||
          [],
        paymentPeriodId: vacancyTemplate?.paymentPeriod?.id.toString() || '',
        scheduleWorkIds:
          vacancyTemplate?.scheduleWorks?.map(({ id }) => id.toString()) || [],
        skills: vacancyTemplate?.skills?.map(({ name }) => name) || [],
        mandatoryRequirements: vacancyTemplate?.mandatoryRequirements || '',
        additionalRequirements: vacancyTemplate?.additionalRequirements || '',
        responsibilities: vacancyTemplate?.responsibilities || '',
      }}
      loading={isPending}
      disabled={isPending}
    />
  );
};
